import type { ComponentChildren } from "preact";
import { t } from "./i18n";
import { Icon } from "./icon";

export function Switch(p: { checked: boolean; disabled?: boolean; label: string; onChange: (v: boolean) => void }) {
  return (
    <label class="switch">
      <input
        type="checkbox"
        role="switch"
        aria-label={p.label}
        checked={p.checked}
        disabled={p.disabled}
        onChange={(e) => p.onChange((e.target as HTMLInputElement).checked)}
      />
      <span />
    </label>
  );
}

export function Segmented<T extends string>(p: { value: T; options: [T, string][]; onChange: (v: T) => void; label: string }) {
  return (
    <div class="segmented" role="group" aria-label={p.label}>
      {p.options.map(([v, text]) => (
        <button type="button" aria-pressed={p.value === v} onClick={() => p.onChange(v)}>
          {text}
        </button>
      ))}
    </div>
  );
}

export function IconButton(p: {
  icon: string;
  label: string;
  onClick: () => void;
  pressed?: boolean;
  kind?: "danger" | "success";
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      class={`icon-btn ${p.kind ?? ""}`}
      aria-pressed={p.pressed === undefined ? undefined : p.pressed}
      disabled={p.disabled}
      onClick={p.onClick}
      title={p.label}
      aria-label={p.label}
    >
      <Icon name={p.icon} size={20} />
      <span class="label">{p.label}</span>
    </button>
  );
}

export function Locked() {
  return (
    <span class="lock">
      <Icon name="lock" size={14} />
      {t("settings.locked")}
    </span>
  );
}

export function Avatar({ name }: { name: string }) {
  const letters = name
    .split(/\s+/)
    .filter((w) => /\p{L}/u.test(w[0] ?? ""))
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("");
  return <span class="avatar">{letters || <Icon name="phone" size={16} />}</span>;
}

export function SettingRow(p: { label: string; children: ComponentChildren; sub?: ComponentChildren }) {
  return (
    <div class="item">
      <div class="grow">
        <div class="title">{p.label}</div>
        {p.sub && <div class="sub">{p.sub}</div>}
      </div>
      {p.children}
    </div>
  );
}

export function duration(s: number): string {
  s = Math.max(0, Math.floor(s));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const mm = h ? String(m).padStart(2, "0") : String(m);
  return `${h ? `${h}:` : ""}${mm}:${String(r).padStart(2, "0")}`;
}
